import axios from "axios";
import { get, writable } from "svelte/store";

import MapDataSource from "./Enums/MapDataSource";
import MapLayer from "./Enums/MapLayer";
import PopupStore from "./Popup/PopupStore";

import AuthStore from "../Auth/Store";
import GeoHelper from "../Helpers/GeoHelper";

import API from "../API/API";

import FilterOperator from "../Filters/FilterOperator";
import FilterPropertyType from "../Filters/FilterPropertyType";
import Filters from "../Filters/Filters";

import roundFloat from "../Helpers/Misc/roundFloat";
import StringHelper from "../Helpers/StringHelper";
import CustomMapDatasetType from "./CustomMapDatasetType";
import MapLayerGroup from "./Enums/MapLayerGroup";
import MapLayerManager from "./MapLayerManager";

const CustomMapDatasets = {};
CustomMapDatasets.lastUpdated = writable(0);
CustomMapDatasets.data = {};
CustomMapDatasets._add = (key) => {
	// TODO: check exists before adding.
	const set = {
		type: CustomMapDatasetType.UNKNOWN,
		key: key,
		value: writable(GeoHelper.emptyGeoJson()),
		visible: writable(true),
		properties: {},
		name: key,
		public: false,
		icon: "",
		filterProperties: [],
		filters: null,
		_contextMenu: null,
		updateStyle: () => {
			const newVal = get(set.layerPaintProperty);
			let fillMaxVal = get(set.layerPaintFillColorMaxValue); // 1.0;
			if (Number.isNaN(parseFloat(fillMaxVal))) {
				fillMaxVal = 1;
			}
			const outlineMaxVal = fillMaxVal; // get(set.layerPaintFillOutlineColorMaxValue); //1.0;
			const map = CustomMapDatasets._map;
			set.layerPaintFillColor = [
				"interpolate", ["linear"],
				// ["get", newVal, 0], // newVal,
				["to-number", ["get", newVal]],
				fillMaxVal * 0,
				"rgba(255,255,204,0)",
				fillMaxVal * 0.2,
				"rgb(255,237,160)",
				fillMaxVal * 0.4,
				"rgb(254,178,76)",
				fillMaxVal * 0.6,
				"rgb(253,141,60)",
				fillMaxVal * 0.9,
				"rgb(252,78,42)",
				fillMaxVal * 1,
				"rgb(227,26,28)"
			];
			set.layerPaintFillOutlineColor = [
				"interpolate", ["linear"],
				["to-number", ["get", newVal]], // ["get", newVal],
				outlineMaxVal * 0,
				"rgba(255,255,204,0)",
				outlineMaxVal * 0.2,
				"rgba(255,237,160,1)",
				outlineMaxVal * 0.4,
				"rgba(254,178,76,1)",
				outlineMaxVal * 0.6,
				"rgba(253,141,60,1)",
				outlineMaxVal * 0.9,
				"rgba(252,78,42,1)",
				outlineMaxVal * 1,
				"rgba(227,26,28,1)"
			];
			map.setPaintProperty(key, "fill-color", set.layerPaintFillColor);
			map.setPaintProperty(key, "fill-outline-color", set.layerPaintFillOutlineColor);
		},
		update: () => {
			const map = CustomMapDatasets._map;
			const data = get(set.value);
			if (data.features.length > 0 && set.filters !== null) {
				const filtered = set.filters.apply(data.features);
				map.getSource(key).setData(GeoHelper.emptyGeoJson(filtered));
			}
			else {
				map.getSource(key).setData(data);
			}
		}
	};

	MapLayer[key] = key;
	MapDataSource[key] = key;
	CustomMapDatasets.data[key] = set;
	CustomMapDatasets.lastUpdated.set(Date.now());
	return set;
};

CustomMapDatasets.get = (key) => {
	let d = CustomMapDatasets.data[key];
	if (typeof d === "undefined") {
		d = CustomMapDatasets._add(key);
	}
	return d;
};
CustomMapDatasets.countPublic = () => {
	let count = 0;
	for (const key in CustomMapDatasets.data) {
		if (CustomMapDatasets.data[key].public) {
			count++;
		}
	}
	return count;
};

// Init temp border force office locs.
CustomMapDatasets.destroy = (map) => {
	const keys = Object.keys(CustomMapDatasets.data);
	for (let i = 0; i < keys.length; i++) {
		const key = keys[i];
		delete MapLayer[key];
		delete MapDataSource[key];
		delete CustomMapDatasets.data[key];
	}
};
CustomMapDatasets.init = (map) => {
	CustomMapDatasets._map = map;

	// Border Force office locations.
	// const id = IdentityHelper.current().name;
	const id = get(AuthStore.userInfo).identity;
	if (id.indexOf("border force") !== -1) {
		const key = "borderforce_offices";
		CustomMapDatasets.get(key);
		// set.type = CustomMapDatasetType.POINTS;
		map.addSource(key, {
			type: "geojson",
			data: GeoHelper.emptyGeoJson()
		});
		map.addLayer({
			id: key,
			type: "symbol",
			source: key,
			layout: {
				visibility: "visible",
				"icon-image": "suitcase-15",
				"icon-size": 1.0,
				"icon-allow-overlap": false
			}
		});
		const onEnter = (e) => {
			const props = e.features[0].properties;
			const coords = e.features[0].geometry.coordinates;
			PopupStore.actions.custom(props, coords, true);
		};
		const onLeave = (e) => {
			PopupStore.actions.custom({}, null, false);
		};

		map.on("mouseenter", key, onEnter);
		map.on("mousemove", key, onEnter);
		map.on("mouseleave", key, onLeave);

		CustomMapDatasets._loadAndSetSource(map, window.location.origin + "/files/BF_offices.json", key);
	}
	// Standard claims data
	else if (id.toLowerCase().indexOf("standard_underwriters") !== -1) {
		CustomMapDatasets.load(map, "claims");
	}
};
CustomMapDatasets.load = (map, key) => {
	if (key === "claims") {
		const portClaimsKey = "std_port_claims";
		const portDataset = CustomMapDatasets.get(portClaimsKey);
		// TODO: feature properties builder?
		portDataset.pointFeatureNumericProperties = [ // TODO: Can we (should we) be dynamically generating this from the dataset properties? Or should it be... user settable?
			"Accomodation Barge",
			"Anchor Handling Tug/Supply (AHTS)",
			"Bitumen Tanker",
			"Bulk Carrier",
			"Bunker barge (propelled)",
			"Cable Layer",
			"Cement Carrier",
			"Chemical Tank Barge",
			"Chemical Tanker",
			"Clean Product Tanker",
			"Coastal Tanker",
			"Conbulker",
			"Container / General Cargo Ship",
			"Container Ship",
			"Crane Barge (non-offshore)",
			"Crane Barge (offshore)",
			"Crew Boat (offshore)",
			"Crude Oil Tanker",
			"Diving Support Vessel",
			"Dredger",
			"Dredger (Offshore)",
			"Dry Barge (dumb)",
			"Dry Barge (propelled)",
			"Fast Ferry",
			"Fishing Boat",
			"Floating Production Storage Offtake (FPSO)",
			"Floating Storage Offtake (FSO)",
			"Floating Storage Regasification unit",
			"Floating Transfer Station",
			"Forest Products Carrier",
			"General Cargo (Heavy Lift)",
			"General Cargo Ship",
			"General Cargo Ship (Oil)",
			"Generator Ship",
			"Great Lakes Only Bulk Carrier",
			"Jack Up Crane Barge",
			"LNG Carrier",
			"LPG Carrier",
			"Lightship",
			"Log Carrier",
			"Multi Role Support Ship",
			"Naval Ship",
			"Newbuilding",
			"Oil Pollution Response Vessel",
			"Oil/Chemical Tanker",
			"Open Hatch Gantry Ship",
			"Ore/Bulk/Oil (Wet and Dry)",
			"Passenger Ferry",
			"Passenger Ship",
			"Pipelayer",
			"Platform Supply Vessel (PSV)",
			"Product Tanker",
			"Rail Ship",
			"Reefer Ship",
			"Research Ship",
			"Ro Ro Cargo",
			"Semi Submersible Heavy Lift",
			"Tank Barge (propelled)",
			"Tug (Offshore)",
			"Tug (non-offshore)",
			"Utility Vessel",
			"Vehicle Carrier",
			"Wind Farm Installation/Service Vessel",
			"Wood Chip Carrier",
			"Workboat",
			"Yacht"
		];
		portDataset.filterProperties = [
			{
				name: "Name",
				key: "name",
				type: FilterPropertyType.STRING,
				typeData: {
					caseSensitive: false
				},
				operators: [FilterOperator.IS, FilterOperator.IS_NOT, FilterOperator.LIKE, FilterOperator.NOT_LIKE]
			},
			{
				name: "Region",
				key: "region_name",
				type: FilterPropertyType.STRING,
				typeData: {
					caseSensitive: false
				},
				operators: [FilterOperator.IS, FilterOperator.IS_NOT, FilterOperator.LIKE, FilterOperator.NOT_LIKE]
			}
			// {
			// 	name: "Total",
			// 	key: "cost",
			// 	type: FilterPropertyType.NUMBER,
			// 	operators: [FilterOperator.GT, FilterOperator.LT]
			// }
		];
		for (let i = 0; i < portDataset.pointFeatureNumericProperties.length; i++) {
			portDataset.filterProperties.push({
				name: StringHelper.replaceAll(portDataset.pointFeatureNumericProperties[i], "_", " "),
				key: portDataset.pointFeatureNumericProperties[i],
				type: FilterPropertyType.NUMBER,
				operators: [FilterOperator.GT, FilterOperator.LT]
			});
		}
		portDataset.type = CustomMapDatasetType.POINTS;
		portDataset.name = "Port Claims";
		portDataset.public = true;
		portDataset.icon = "monetization_on";
		portDataset.filters = new Filters(portDataset.filterProperties);
		portDataset.filters.usingMagicGetters = false;

		portDataset.filters.lastUpdated.subscribe((v) => {
			if (!v) return;
			portDataset.update();
		});
		CustomMapDatasets.lastUpdated.set(Date.now());

		// Port claims
		map.addSource(portClaimsKey, {
			type: "geojson",
			data: get(portDataset.value)
		});
		map.addLayer({
			id: portClaimsKey,
			type: "symbol",
			source: portClaimsKey,
			layout: {
				visibility: "visible",
				"icon-image": "border-dot-13",
				"icon-size": 1.3,
				"icon-allow-overlap": false
			}
		});

		const onEnter = (e) => {
			const props = e.features[0].properties;
			const coords = e.features[0].geometry.coordinates;

			const groups = JSON.parse(props.groups);
			let html = "";
			html += `<strong>${props.name}</strong><br/>`;
			html += `<i>${props.region_name}</i><br/><br/>`;
			html += "<strong>Groups:</strong><br/>";
			html += "<table>";
			for (let i = 0; i < groups.length; i++) {
				html += `<tr>
							<td>${groups[i].type}</td>
							<td>$${roundFloat(groups[i].cost, 4)}/port call</td>
						 </tr>`;
			}
			html += "</table>";
			PopupStore.actions.customHtml(html, 400, coords, true);
		};
		const onLeave = (e) => {
			PopupStore.actions.custom({}, null, false);
		};
		map.on("mouseenter", portClaimsKey, onEnter);
		map.on("mousemove", portClaimsKey, onEnter);
		map.on("mouseleave", portClaimsKey, onLeave);
		API.get("datasets/std-port-claims").then((response) => {
			const data = JSON.parse(response.dataset.content);
			portDataset.value.set(data);
			portDataset.update();
		});
		// ----------------------------------------
		// Regional claims
		// ----------------------------------------
		const regionalClaimsKey = "std_regional_claims";
		const regionalDataset = CustomMapDatasets.get(regionalClaimsKey);
		regionalDataset.filterProperties = [
			{
				name: "Name",
				key: "region_name",
				type: FilterPropertyType.STRING,
				typeData: {
					caseSensitive: false
				},
				operators: [FilterOperator.IS, FilterOperator.IS_NOT, FilterOperator.LIKE, FilterOperator.NOT_LIKE]
			}
		];
		const numericFields = [
			"All_Vessels", "Barge_Group", "Bulk_Cargo_Group", "General_Cargo_Group", "Liquid_Cargo_Group", "Miscellaneous", "Offshore_Group", "Passenger_and_Ferry_Group", "Small_Ships_Group", "Speciality"
		];
		for (let i = 0; i < numericFields.length; i++) {
			regionalDataset.filterProperties.push({
				name: StringHelper.replaceAll(numericFields[i], "_", " "),
				key: numericFields[i],
				type: FilterPropertyType.NUMBER,
				operators: [FilterOperator.GT, FilterOperator.LT]
			});
		}
		regionalDataset.type = CustomMapDatasetType.MULTIPOLYGON_GRADIENT;
		regionalDataset.name = "Regional Claims";
		regionalDataset.public = true;
		regionalDataset.icon = "monetization_on";
		regionalDataset.filters = new Filters(regionalDataset.filterProperties);
		regionalDataset.filters.usingMagicGetters = false;
		regionalDataset.filters.lastUpdated.subscribe((v) => {
			if (!v) return;
			regionalDataset.update();
		});
		regionalDataset.layerPaintProperty = writable("All_Vessels");
		regionalDataset.layerPaintProperties = numericFields; // ["All_Vessels", "Bulk_Cargo_Group"];
		regionalDataset.layerPaintFillColor = "white";
		regionalDataset.layerPaintFillOutlineColor = "white";
		regionalDataset.layerPaintFillColorMaxValue = writable(1.0);
		regionalDataset.layerPaintFillColorMaxValueDynamic = true;
		CustomMapDatasets.lastUpdated.set(Date.now());

		map.addSource(regionalClaimsKey, {
			type: "geojson",
			data: get(regionalDataset.value)
		});
		map.addLayer({
			id: regionalClaimsKey,
			type: "fill",
			source: regionalClaimsKey,
			layout: {
				visibility: "visible"
			},
			paint: {
				"fill-color": regionalDataset.layerPaintFillColor,
				"fill-opacity": 0.25,
				"fill-outline-color": regionalDataset.layerPaintFillOutlineColor
			}
		});

		MapLayerManager.addLayerGroup(MapLayerGroup.CUSTOM_STANDARD_CLAIMS, [portClaimsKey, regionalClaimsKey], [portClaimsKey, regionalClaimsKey]);

		const updateMaxValue = () => {
			const property = get(regionalDataset.layerPaintProperty);
			if (regionalDataset.layerPaintFillColorMaxValueDynamic) {
				let max = 1;
				// let maxFeature = null;
				const data = get(regionalDataset.value);
				for (let i = 0; i < data.features.length; i++) {
					const val = data.features[i].properties[property];
					if (val >= max) {
						max = val;
						// maxFeature = data.features[i];
					}
				}
				regionalDataset.layerPaintFillColorMaxValue.set(max);
				CustomMapDatasets.lastUpdated.set(Date.now());
			}
		};

		regionalDataset.layerPaintProperty.subscribe((newVal) => {
			updateMaxValue();
			regionalDataset.updateStyle();
		});
		regionalDataset.layerPaintFillColorMaxValue.subscribe((newVal) => {
			regionalDataset.updateStyle();
		});
		regionalDataset.value.subscribe(() => {
			updateMaxValue();
		});

		const onClick = (e) => {
			const props = e.features[0].properties;
			const coords = [e.lngLat.lng, e.lngLat.lat];

			let html = "";
			html += `<strong>${props.region_name}</strong><br/>`;
			html += "<table>";
			for (const key in props) {
				if (key === "region_name") {
					continue;
				}
				html += `<tr>
							<td>${StringHelper.replaceAll(key, "_", " ")}</td>
							<td>$${roundFloat(props[key], 4)}/day</td>
						 </tr>`;
			}
			html += "</table>";
			PopupStore.actions.customHtml(html, 400, coords, true);
		};

		const finishLoading = (jsonContent) => {
			const data = JSON.parse(jsonContent);
			regionalDataset.value.set(data);
			regionalDataset.update();
		};
		map.on("click", regionalClaimsKey, onClick);
		map.on("mouseleave", regionalClaimsKey, onLeave);
		API.get("datasets/std-regional-claims?page=1").then((response) => {
			let jsonContent = response.dataset.content;
			if (response.dataset.pagination.total > 1) {
				API.get("datasets/std-regional-claims?page=2").then((response) => {
					jsonContent += response.dataset.content;
					finishLoading(jsonContent);
					// CustomMapDatasets.lastUpdated.set(Date.now());
				});
			}
			else {
				finishLoading(jsonContent);
			}
		});
	}
};

CustomMapDatasets._loadAndSetSource = (map, fileUrl, sourceKey) => {
	axios.get(fileUrl)
		.then(response => {
			return { success: (response.status === 200), data: response.data };
		})
		.then((response) => {
			if (!response.success) {
				console.warning(sourceKey + " failed", response);
				return;
			}
			map.getSource(sourceKey).setData(response.data);
		})
		.catch(error => {
			console.error(sourceKey + " failed", error);
		});
};

export default CustomMapDatasets;
